import React, {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
} from 'react';
import dayjs from 'dayjs';
import { Loader } from '../../../../components/Loader/Loader';
import '../Widget.scss';
import './OrderOverviewWidget.scss';
import { regionNames, zoneNames } from 'types/orderTypes';
import { Link } from 'react-router-dom';
import { CalendarInputComponent } from '../../../../ReusableComponents/CalendarInput/CalendarInputComponent';
import { AccordionComponent } from '../../../../ReusableComponents/Accordion/AccordionComponent';
import { MdExpandMore } from 'react-icons/md';
import { capitalize } from '../../../../utils/strings';
import { ButtonComponent } from '../../../../ReusableComponents/Button/ButtonComponent';
import { RegionZoneIntervalOrderCount } from '../../../../types/statsTypes';
import { fetchOrderCountRegion } from '../../../../fetch/fetchStats';

export const OrderOverviewWidget: FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [orderCountRegion, setOrderCountRegion] = useState<
    RegionZoneIntervalOrderCount[]
  >([]);

  useEffect(() => {
    setIsLoading(true);
    fetchOrderCountRegion(date)
      .then(result => {
        setOrderCountRegion(result);
        setIsLoading(false);
      })
      .catch(error => console.log(error));
  }, [date]);

  const setDateCallback = useCallback(
    (dateParam: string) => setDate(dateParam),
    []
  );

  const createAccordionWindowsList = useCallback(() => {
    const accordionArray = [];
    orderCountRegion.forEach(({ region, intervals, count }) => {
      const productOrderCount = count['delivery']?.orderCount ?? 0;
      const largeOrderCount = count['large']?.orderCount ?? 0;
      const totalOrderCount = productOrderCount + largeOrderCount;
      if (intervals.length) {
        const accordionObject = {
          expandIcon: <MdExpandMore />,
          accordionSummary: {
            element: (
              <div className="order-widget__window-summary-content">
                <div>{capitalize(region)}</div>
                <div>{productOrderCount}</div>
                <div>{largeOrderCount}</div>
                <div>{totalOrderCount}</div>
              </div>
            ),
            className: 'order-widget__accordion-summary',
          },
          accordionDetails: {
            className: 'order-widget__accordion-details',
            element: intervals.map(({ start, end, orderCount }) => {
              const productOrderCount = orderCount['delivery'] ?? 0;
              const largeOrderCount = orderCount['large'] ?? 0;
              const totalOrderCount = productOrderCount + largeOrderCount;
              return (
                <div
                  key={`${start}-${end}-${region}`}
                  className="order-widget__window-wrapper"
                >
                  <div className="order-widget__window">
                    {dayjs(start).format('HH:mm')} -{dayjs(end).format('HH:mm')}
                  </div>
                  <div className="order-widget__window-order-count">
                    {productOrderCount}
                  </div>
                  <div className="order-widget__window-order-count">
                    {largeOrderCount}
                  </div>
                  <div className="order-widget__window-order-count">
                    {totalOrderCount}
                  </div>
                </div>
              );
            }),
          },
        };
        accordionArray.push(accordionObject);
      }
    });
    return accordionArray;
  }, [orderCountRegion]);

  const createAccordionRegionsList = useCallback(() => {
    const accordionArray = [];
    orderCountRegion.forEach(({ region, count, zoneOrderCounts }) => {
      //Interim solution until we visualize different products
      const productOrderCount = count['delivery']?.orderCount ?? 0;
      const largeOrderCount = count['large']?.orderCount ?? 0;
      const totalOrderCount = productOrderCount + largeOrderCount;
      const accordionObject = {
        expandIcon: <MdExpandMore />,
        accordionSummary: {
          element: (
            <div className="order-widget__zone-summary-content">
              <div>{capitalize(regionNames[region])}</div>
              <div>{productOrderCount}</div>
              <div>{largeOrderCount}</div>
              <div>{totalOrderCount}</div>
            </div>
          ),
          className: 'order-widget__accordion-summary',
        },
        accordionDetails: {
          className: 'order-widget__accordion-details',
          element: zoneOrderCounts.map((zoneOrderCount, index) => {
            const productOrderCount =
              zoneOrderCount.orderCount['delivery'] ?? 0;
            const largeOrderCount = zoneOrderCount.orderCount['large'] ?? 0;
            const totalOrderCount = productOrderCount + largeOrderCount;
            return (
              <div
                key={`${zoneNames[zoneOrderCount.zone]}-${index}`}
                className="order-widget__zone-wrapper"
              >
                <div className="order-widget__zone">
                  {zoneNames[zoneOrderCount.zone]}
                </div>
                <div className="order-widget__zone-order-count">
                  {productOrderCount}
                </div>
                <div className="order-widget__zone-order-count">
                  {largeOrderCount}
                </div>
                <div className="order-widget__zone-order-count">
                  {totalOrderCount}
                </div>
              </div>
            );
          }),
        },
      };
      accordionArray.push(accordionObject);
    });
    return accordionArray;
  }, [orderCountRegion]);

  return (
    <div className="widget order-widget">
      <div className="order-widget__header">
        <h5>Order overview</h5>
      </div>
      <CalendarInputComponent date={date} setDate={setDateCallback} />
      {isLoading ? (
        <div className="order-widget__loader">
          <Loader type="spinner" size="xxl" />
        </div>
      ) : (
        <>
          <div className="order-widget__orders-by-windows">
            <h6>Orders in time window</h6>
            <div className="order-widget-header">
              <span>Region</span>
              <span>Regular</span>
              <span>Large</span>
              <span>Total</span>
            </div>
            <AccordionComponent
              accordionDataList={createAccordionWindowsList()}
            />
          </div>
          <div className="order-widget__orders-by-regions">
            <h6>Orders by region</h6>
            <div className="order-widget-header">
              <span>Region</span>
              <span>Regular</span>
              <span>Large</span>
              <span>Total</span>
            </div>
            <AccordionComponent
              accordionDataList={createAccordionRegionsList()}
            />
          </div>
          <div className="order-widget__buttons">
            <ButtonComponent
              text="Manage capacity"
              component={Link}
              to="/dashboard/capacity-management"
            />
          </div>
        </>
      )}
    </div>
  );
};
